import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { ColumnDef, PaginationState, SortingState, ColumnFiltersState } from "@tanstack/react-table";
import {
  Plus,
  Search,
  Download,
  Eye,
  Pencil,
  Trash2,
  Receipt,
  FileText,
  PieChart,
  RefreshCw,
  Printer,
  Loader2,
  Upload,
  Paperclip,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Wallet,
  DollarSign,
  Percent,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";

const EXPENSE_CATEGORIES_COLORS: Record<string, string> = {
  Administrative: "#3b82f6",
  Meeting: "#8b5cf6",
  Emergency: "#ef4444",
  Investment: "#10b981",
  Other: "#6b7280",
  Utilities: "#f59e0b",
  Transport: "#06b6d4",
  Supplies: "#ec4899",
};

const getColorForCategory = (cat: string) =>
  EXPENSE_CATEGORIES_COLORS[cat] || "#6b7280";

const expenseFormSchema = z.object({
  description: z.string().min(2, "Description must be at least 2 characters"),
  amount: z.string().min(1, "Amount is required").refine(
    (val) => {
      const num = Number(val);
      return !isNaN(num) && num > 0;
    },
    "Amount must be greater than 0"
  ),
  category: z.string().min(1, "Category is required"),
  paymentMethodId: z.string().min(1, "Payment method is required"),
  paidTo: z.string().min(1, "Paid To is required"),
  expenseDate: z.string().min(1, "Date is required"),
  notes: z.string().optional(),
  receiptNumber: z.string().optional(),
});

type ExpenseFormValues = z.infer<typeof expenseFormSchema>;

interface ExpenseRecord {
  id: number;
  expenseId: string;
  description: string;
  amount: number;
  expenseDate: string;
  category: string;
  categoryId: number | null;
  paymentMethod: string | null;
  paymentMethodId: number | null;
  paidTo: string | null;
  receiptNumber: string | null;
  notes: string | null;
  receiptPath: string | null;
  isApproved: boolean;
  recordedBy: { firstName: string; lastName: string; email: string } | null;
  createdAt: string;
  proofFileName: string | null;
  proofFilePath: string | null;
  proofMimeType: string | null;
  proofSize: number | null;
  proofUploadedAt: string | null;
  hasProof: boolean;
}

interface SummaryData {
  totalInterestAccumulated: number;
  totalExpenses: number;
  totalExpensesCount: number;
  netInterestAfterExpenses: number;
  withholdingAmount: number;
  availableForDistribution: number;
  expensesByCategory: { category: string; totalAmount: number; count: number }[];
}

export default function Expenses() {
  const queryClient = useQueryClient();
  const confirmModal = useConfirmModal();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseRecord | null>(null);
  const [viewingExpense, setViewingExpense] = useState<ExpenseRecord | null>(null);

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    const timeout = setTimeout(() => {
      setDebouncedSearch(val);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 400);
    return () => clearTimeout(timeout);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setCategoryFilter("");
    setPaymentMethodFilter("");
    setStartDate("");
    setEndDate("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const hasActiveFilters = debouncedSearch || categoryFilter || paymentMethodFilter || startDate || endDate;

  const { data: summaryData, isLoading: summaryLoading } = useQuery<SummaryData>(
    ["expenses-summary", startDate, endDate],
    async () => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const { data } = await api.get(`/expenses/summary?${params.toString()}`);
      return data.data;
    }
  );

  const { data: categories = [] } = useQuery(
    ["expense-categories-list"],
    async () => {
      const { data } = await api.get("/expenses/categories");
      return data.results || [];
    }
  );

  const { data: paymentMethods = [] } = useQuery(
    ["payment-methods-list"],
    async () => {
      const { data } = await api.get("/payment-methods", {
        params: { isActive: "true", page_size: 100 },
      });
      return data.results || [];
    }
  );

  const expensesQuery = useQuery(
    [
      "expenses",
      debouncedSearch,
      categoryFilter,
      paymentMethodFilter,
      startDate,
      endDate,
      pageIndex,
      pageSize,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(pageIndex + 1));
      params.set("page_size", String(pageSize));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (categoryFilter) params.set("category", categoryFilter);
      if (paymentMethodFilter) params.set("paymentMethod", paymentMethodFilter);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const { data } = await api.get(`/expenses?${params.toString()}`);
      return data;
    },
    { keepPreviousData: true }
  );

  const expenses: ExpenseRecord[] = expensesQuery.data?.results || [];
  const pagination = expensesQuery.data?.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 1 };

  const deleteMutation = useMutation(
    async (id: number) => {
      await api.delete(`/expenses/${id}`);
    },
    {
      onSuccess: () => {
        toast.success("Expense deleted successfully");
        queryClient.invalidateQueries("expenses");
        queryClient.invalidateQueries("expenses-summary");
        confirmModal.close();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to delete expense");
        confirmModal.setIsLoading(false);
      },
    }
  );

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (categoryFilter) params.set("category", categoryFilter);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const { data } = await api.get(`/expenses/export?${params.toString()}`);
      const exportData = data.data || [];

      if (exportData.length === 0) {
        toast.warning("No expenses to export");
        return;
      }

      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Expenses");
      XLSX.writeFile(wb, `expenses_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      toast.success("Expenses exported successfully");
    } catch {
      toast.error("Failed to export expenses");
    }
  };

  const categoryChartData = useMemo(() => {
    if (!summaryData?.expensesByCategory) return [];
    return summaryData.expensesByCategory.map((item) => ({
      name: item.category,
      value: item.totalAmount,
    }));
  }, [summaryData]);

  const columns: ColumnDef<ExpenseRecord>[] = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "#",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        enableSorting: false,
        enableHiding: false,
        size: 48,
      },
      {
        accessorKey: "expenseDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date & Time" />,
        cell: ({ row }) => {
          const d = row.original.expenseDate;
          return <span className="text-sm text-slate-700">{d ? format(new Date(d), "MMM dd, yyyy") : "—"}</span>;
        },
      },
      {
        accessorKey: "expenseId",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Expense ID" />,
        cell: ({ row }) => (
          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.expenseId}
          </span>
        ),
      },
      {
        accessorKey: "category",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
        cell: ({ row }) => {
          const cat = row.original.category;
          return (
            <Badge
              variant="outline"
              className="text-xs font-medium border-0"
              style={{
                backgroundColor: `${getColorForCategory(cat)}15`,
                color: getColorForCategory(cat),
              }}
            >
              {cat}
            </Badge>
          );
        },
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-700 max-w-[200px] truncate block">
            {row.original.description || "—"}
          </span>
        ),
      },
      {
        accessorKey: "paymentMethod",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Payment Method" />,
        cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.paymentMethod || "—"}</span>,
      },
      {
        accessorKey: "paidTo",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Paid To" />,
        cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.paidTo || "—"}</span>,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Amount (FRW)" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-slate-900">{formatCurrency(row.original.amount)}</span>
        ),
      },
      {
        id: "proof",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Proof" />,
        cell: ({ row }) => {
          const exp = row.original;
          return exp.hasProof ? (
            <a
              href={`${api.defaults.baseURL}/expenses/${exp.id}/proof`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Paperclip className="h-3 w-3" />
              <span className="text-xs">View</span>
            </a>
          ) : (
            <span className="text-xs text-slate-400">No proof</span>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "recordedBy",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Recorded By" />,
        cell: ({ row }) => {
          const rb = row.original.recordedBy;
          return (
            <span className="text-sm text-slate-600">
              {rb ? `${rb.firstName} ${rb.lastName || ""}`.trim() : "—"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
        cell: ({ row }) => {
          const exp = row.original;
          return (
            <div className="flex items-center justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z" fill="currentColor" />
                    </svg>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => setViewingExpense(exp)} className="cursor-pointer text-sm">
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setEditingExpense(exp)} className="cursor-pointer text-sm">
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => confirmModal.open({ meta: exp })}
                    className="cursor-pointer text-sm text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [pageIndex, pageSize, setViewingExpense, setEditingExpense, confirmModal]
  );

  return (
    <>
      <ConfirmModal
        title="Delete Expense"
        description="Are you sure you want to delete this expense? This action cannot be undone."
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          confirmModal.setIsLoading(true);
          deleteMutation.mutate(meta.id);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <RecordExpenseModal
        open={showRecordModal || !!editingExpense}
        onClose={() => {
          setShowRecordModal(false);
          setEditingExpense(null);
        }}
        editingExpense={editingExpense}
        categories={categories}
        paymentMethods={paymentMethods}
        onSuccess={() => {
          setShowRecordModal(false);
          setEditingExpense(null);
          queryClient.invalidateQueries("expenses");
          queryClient.invalidateQueries("expenses-summary");
        }}
      />

      {viewingExpense && (
        <ViewExpenseModal
          expense={viewingExpense}
          onClose={() => setViewingExpense(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Expenses</h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage all cooperative expenses that are paid from the main account.
              </p>
            </div>
            <Button
              onClick={() => setShowRecordModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Record Expense
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <SummaryCard
              title="Interest Accumulated"
              value={formatCurrency(summaryData?.totalInterestAccumulated || 0)}
              icon={<TrendingUp className="h-5 w-5" />}
              color="blue"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Total Expenses"
              value={formatCurrency(summaryData?.totalExpenses || 0)}
              icon={<Wallet className="h-5 w-5" />}
              color="red"
              loading={summaryLoading}
              subtitle={`${summaryData?.totalExpensesCount || 0} transactions`}
            />
            <SummaryCard
              title="Net Interest After Expenses"
              value={formatCurrency(summaryData?.netInterestAfterExpenses || 0)}
              icon={<TrendingDown className="h-5 w-5" />}
              color="emerald"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Available for Distribution"
              value={formatCurrency(summaryData?.availableForDistribution || 0)}
              icon={<DollarSign className="h-5 w-5" />}
              color="violet"
              loading={summaryLoading}
            />
            <SummaryCard
              title="Withholding (10%)"
              value={formatCurrency(summaryData?.withholdingAmount || 0)}
              icon={<Percent className="h-5 w-5" />}
              color="amber"
              loading={summaryLoading}
            />
          </div>

          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search expenses..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9 h-9 bg-slate-50 border-slate-200"
                  />
                </div>
              </div>
              <div className="min-w-[160px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">From Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPagination((prev) => ({ ...prev, pageIndex: 0 })); }}
                  className="h-9 bg-slate-50 border-slate-200"
                />
              </div>
              <div className="min-w-[160px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">To Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPagination((prev) => ({ ...prev, pageIndex: 0 })); }}
                  className="h-9 bg-slate-50 border-slate-200"
                />
              </div>
              <div className="min-w-[170px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
                <Select
                  value={categoryFilter}
                  onValueChange={(val) => { setCategoryFilter(val === "all" ? "" : val); setPagination((prev) => ({ ...prev, pageIndex: 0 })); }}
                >
                  <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat: any) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[170px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Payment Method</label>
                <Select
                  value={paymentMethodFilter}
                  onValueChange={(val) => { setPaymentMethodFilter(val === "all" ? "" : val); setPagination((prev) => ({ ...prev, pageIndex: 0 })); }}
                >
                  <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All Methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    {paymentMethods.map((pm: any) => (
                      <SelectItem key={pm.id} value={pm.name}>
                        {pm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <Download className="h-4 w-4 mr-1" />
                Export Excel
              </Button>
            </div>
          </div>

          {/* Main Content: Table + Side Panel */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
            {/* Expense Table */}
            <DataTable
              columns={columns}
              data={expenses}
              isLoading={expensesQuery.isLoading}
              isFetching={expensesQuery.isFetching}
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageCount={pagination.totalPages || 1}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              onSearch={(val) => {
                setSearch(val);
                setDebouncedSearch(val);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              emptyMessage="No expenses found"
              emptyDescription={hasActiveFilters ? "Try adjusting your filters" : "Record your first expense to get started"}
              Action={() => (
                <Button
                  onClick={() => setShowRecordModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Record Expense
                </Button>
              )}
            />

            {/* Right Side Panel */}
            <div className="space-y-6">
              {/* Expense Summary */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-100 rounded-lg">
                      <FileText className="h-4 w-4 text-emerald-600" />
                    </div>
                    Expense Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Total Expenses</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {formatCurrency(summaryData?.totalExpenses || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Transactions</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {summaryData?.totalExpensesCount || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Interest Earned</span>
                    <span className="text-sm font-semibold text-emerald-600">
                      {formatCurrency(summaryData?.totalInterestAccumulated || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Net After Expenses</span>
                    <span className={`text-sm font-semibold ${(summaryData?.netInterestAfterExpenses || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(summaryData?.netInterestAfterExpenses || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-500">For Distribution</span>
                    <span className="text-sm font-bold text-emerald-600">
                      {formatCurrency(summaryData?.availableForDistribution || 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Expenses by Category */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <div className="p-1.5 bg-violet-100 rounded-lg">
                      <PieChart className="h-4 w-4 text-violet-600" />
                    </div>
                    Expenses by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {categoryChartData.length > 0 ? (
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                          <Pie
                            data={categoryChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {categoryChartData.map((entry, idx) => (
                              <Cell
                                key={idx}
                                fill={getColorForCategory(entry.name)}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              fontSize: "12px",
                            }}
                          />
                          <Legend
                            iconType="circle"
                            iconSize={8}
                            formatter={(value: string) => (
                              <span className="text-xs text-slate-600">{value}</span>
                            )}
                          />
                        </RePieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-8 text-center">
                      <PieChart className="h-8 w-8 text-slate-300 mb-2" />
                      <p className="text-xs text-slate-400">No expense data</p>
                    </div>
                  )}
                  {summaryData?.expensesByCategory && summaryData.expensesByCategory.length > 0 && (
                    <div className="space-y-2 mt-3 pt-3 border-t border-slate-100">
                      {summaryData.expensesByCategory.map((cat) => (
                        <div key={cat.category} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: getColorForCategory(cat.category) }}
                            />
                            <span className="text-xs text-slate-600">{cat.category}</span>
                          </div>
                          <span className="text-xs font-medium text-slate-900">
                            {formatCurrency(cat.totalAmount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-900">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    onClick={() => setShowRecordModal(true)}
                    className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Record New Expense
                  </Button>
                  <Button
                    onClick={handleExport}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-slate-200"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export to Excel
                  </Button>
                  <Button
                    onClick={() => window.print()}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-slate-200"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print Report
                  </Button>
                  <Button
                    onClick={() => {
                      queryClient.invalidateQueries("expenses");
                      queryClient.invalidateQueries("expenses-summary");
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start border-slate-200"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Data
                  </Button>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-900">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Expenses reduce distributable interest before member distribution.
                    Withholding tax (10%) is calculated after deducting expenses from accumulated interest.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  color,
  loading,
  subtitle,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  subtitle?: string;
}) {
  const colorMap: Record<string, { bg: string; iconBg: string; iconText: string; valueText: string }> = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", iconText: "text-blue-600", valueText: "text-blue-900" },
    red: { bg: "bg-red-50", iconBg: "bg-red-100", iconText: "text-red-600", valueText: "text-red-900" },
    emerald: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", iconText: "text-emerald-600", valueText: "text-emerald-900" },
    violet: { bg: "bg-violet-50", iconBg: "bg-violet-100", iconText: "text-violet-600", valueText: "text-violet-900" },
    amber: { bg: "bg-amber-50", iconBg: "bg-amber-100", iconText: "text-amber-600", valueText: "text-amber-900" },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <Card className={`border border-slate-200 shadow-sm ${c.bg}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 mb-1 truncate">{title}</p>
            {loading ? (
              <div className="h-7 w-24 bg-slate-200 rounded animate-pulse" />
            ) : (
              <p className={`text-lg font-bold ${c.valueText} truncate`}>{value}</p>
            )}
            {subtitle && (
              <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className={`p-2 rounded-lg ${c.iconBg} flex-shrink-0`}>
            <span className={c.iconText}>{icon}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RecordExpenseModal({
  open,
  onClose,
  editingExpense,
  categories,
  paymentMethods,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  editingExpense: ExpenseRecord | null;
  categories: any[];
  paymentMethods: any[];
  onSuccess: () => void;
}) {
  const isEditing = !!editingExpense;
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);

  const getDefaultValues = useCallback(
    (): ExpenseFormValues => ({
      description: editingExpense?.description || "",
      amount: editingExpense ? String(editingExpense.amount || "") : "",
      category: editingExpense?.category || "",
      paymentMethodId: editingExpense?.paymentMethodId ? String(editingExpense.paymentMethodId) : "",
      paidTo: editingExpense?.paidTo || "",
      expenseDate: editingExpense?.expenseDate
        ? format(new Date(editingExpense.expenseDate), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
      notes: editingExpense?.notes || "",
      receiptNumber: editingExpense?.receiptNumber || "",
    }),
    [editingExpense]
  );

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
      setProofFile(null);
      setProofPreview(null);
    }
  }, [open, editingExpense]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF, JPG, PNG, and WEBP files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    setProofFile(file);
    if (file.type.startsWith("image/")) {
      setProofPreview(URL.createObjectURL(file));
    } else {
      setProofPreview(null);
    }
  };

  const removeProof = () => {
    setProofFile(null);
    setProofPreview(null);
  };

  const mutation = useMutation(
    async (values: ExpenseFormValues) => {
      const trimmedDescription = values.description.trim();
      const trimmedName = trimmedDescription;

      if (proofFile) {
        const formData = new FormData();
        formData.append("description", trimmedDescription);
        formData.append("name", trimmedName);
        formData.append("amount", String(Number(values.amount)));
        formData.append("expenseDate", values.expenseDate);
        formData.append("category", values.category);
        formData.append("paymentMethodId", String(Number(values.paymentMethodId)));
        formData.append("paidTo", values.paidTo.trim());
        formData.append("vendor", values.paidTo.trim());
        formData.append("receiptNumber", values.receiptNumber?.trim() || "");
        formData.append("notes", values.notes?.trim() || "");
        formData.append("proof", proofFile);

        if (import.meta.env.DEV) {
          console.log("[Expense FormData]", Array.from(formData.entries()));
        }

        if (isEditing) {
          return api.put(`/expenses/${editingExpense.id}`, formData);
        }
        return api.post("/expenses", formData);
      }

      const payload = {
        description: trimmedDescription,
        name: trimmedName,
        amount: Number(values.amount),
        expenseDate: values.expenseDate,
        category: values.category,
        paymentMethodId: Number(values.paymentMethodId),
        paidTo: values.paidTo.trim(),
        vendor: values.paidTo.trim(),
        receiptNumber: values.receiptNumber?.trim() || null,
        notes: values.notes?.trim() || null,
      };

      if (isEditing) {
        return api.put(`/expenses/${editingExpense.id}`, payload);
      }
      return api.post("/expenses", payload);
    },
    {
      onSuccess: (response) => {
        const msg = response?.data?.message || (isEditing ? "Expense updated successfully" : "Expense recorded successfully");
        toast.success(msg);
        onSuccess();
        form.reset(getDefaultValues());
        setProofFile(null);
        setProofPreview(null);
      },
      onError: (err: any) => {
        const serverMsg = err?.response?.data?.message;
        const serverErr = err?.response?.data?.error;
        const detail = serverMsg || serverErr || err?.message || "Failed to save expense";
        console.error("[Expense Save Error]", err?.response?.data || err);
        toast.error(detail);
      },
    }
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.handleSubmit((values) => mutation.mutate(values))();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[760px] w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl border border-slate-200 shadow-xl">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle className="text-lg font-bold text-slate-900">
            {isEditing ? "Edit Expense" : "Record Expense"}
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-0.5">
            {isEditing ? "Update the expense details below" : "Fill in the details to record a new expense"}
          </p>
        </DialogHeader>

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <Form {...form}>
            <form
              id="expense-record-form"
              onSubmit={handleFormSubmit}
              className="space-y-5"
            >
              {/* Description — full width */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-700">
                      Description <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Office supplies for July"
                        {...field}
                        className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              {/* Amount + Date — two columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">
                        Amount (FRW) <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">FRW</span>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="0"
                            {...field}
                            className="h-10 pl-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="expenseDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">
                        Date <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Category + Payment Method — two columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">
                        Category <span className="text-red-500">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((cat: any) => (
                            <SelectItem key={cat.id} value={cat.name}>
                              {cat.name}
                            </SelectItem>
                          ))}
                          {categories.length === 0 && (
                            <>
                              <SelectItem value="Administrative">Administrative</SelectItem>
                              <SelectItem value="Meeting">Meeting</SelectItem>
                              <SelectItem value="Emergency">Emergency</SelectItem>
                              <SelectItem value="Investment">Investment</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paymentMethodId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">
                        Payment Method <span className="text-red-500">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {paymentMethods.map((pm: any) => (
                            <SelectItem key={pm.id} value={String(pm.id)}>
                              {pm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Paid To + Receipt — two columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="paidTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">
                        Paid To <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Vendor or recipient name"
                          {...field}
                          className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receiptNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-700">Receipt #</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Optional"
                          {...field}
                          className="h-10 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Notes — full width */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-700">Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Optional notes about this expense..."
                        className="resize-none h-20 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              {/* Payment Proof — full width */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Payment Proof</label>
                <p className="text-xs text-slate-400">Optional. PDF, JPG, PNG, or WEBP. Max 5MB.</p>

                {proofFile ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    {proofPreview ? (
                      <img src={proofPreview} alt="Proof preview" className="h-12 w-12 rounded object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-emerald-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{proofFile.name}</p>
                      <p className="text-xs text-slate-400">{(proofFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={removeProof}
                      className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
                    <Upload className="h-5 w-5 text-slate-400 mb-1" />
                    <span className="text-xs text-slate-500">Click to upload proof file</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleFileSelect}
                    />
                  </label>
                )}

                {editingExpense?.hasProof && !proofFile && (
                  <div className="flex items-center gap-2 text-xs text-emerald-600">
                    <Paperclip className="h-3 w-3" />
                    <span>Existing proof attached: {editingExpense.proofFileName}</span>
                  </div>
                )}
              </div>
            </form>
          </Form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3 rounded-b-2xl">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isLoading}
            className="border-slate-200 text-slate-600 hover:bg-slate-100 h-10 px-5"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="expense-record-form"
            disabled={mutation.isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-6 font-semibold shadow-sm"
          >
            {mutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              isEditing ? "Update Expense" : "Record Expense"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewExpenseModal({
  expense,
  onClose,
}: {
  expense: ExpenseRecord;
  onClose: () => void;
}) {
  const proofUrl = `${api.defaults.baseURL}/expenses/${expense.id}/proof`;
  const isImage = expense.proofMimeType?.startsWith("image/");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[760px] w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl border border-slate-200 shadow-xl">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">Expense Details</DialogTitle>
              <p className="text-sm text-slate-500 mt-0.5">
                Full breakdown of this expense record
              </p>
            </div>
            <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">
              {expense.expenseId}
            </span>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Summary card */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Receipt className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-500 truncate">
                  {expense.description || "No description"}
                </p>
                <p className="text-xl font-bold text-slate-900 truncate">
                  {formatCurrency(expense.amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge
                variant="outline"
                className="text-xs font-medium border-0"
                style={{
                  backgroundColor: `${getColorForCategory(expense.category)}15`,
                  color: getColorForCategory(expense.category),
                }}
              >
                {expense.category}
              </Badge>
              <Badge
                variant="outline"
                className={`text-xs font-medium border-0 ${
                  expense.hasProof
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {expense.hasProof ? "Proof Attached" : "No Proof"}
              </Badge>
            </div>
          </div>

          {/* Expense Information */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Expense Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <DetailRow label="Date">
                {expense.expenseDate
                  ? format(new Date(expense.expenseDate), "MMM dd, yyyy")
                  : "—"}
              </DetailRow>
              <DetailRow label="Category">
                <Badge
                  variant="outline"
                  className="text-xs font-medium border-0"
                  style={{
                    backgroundColor: `${getColorForCategory(expense.category)}15`,
                    color: getColorForCategory(expense.category),
                  }}
                >
                  {expense.category}
                </Badge>
              </DetailRow>
              <DetailRow label="Description">
                <span className="break-words">{expense.description || "—"}</span>
              </DetailRow>
              <DetailRow label="Paid To">{expense.paidTo || "—"}</DetailRow>
              <DetailRow label="Payment Method">{expense.paymentMethod || "—"}</DetailRow>
              <DetailRow label="Receipt #">{expense.receiptNumber || "—"}</DetailRow>
              <DetailRow label="Recorded By">
                {expense.recordedBy
                  ? `${expense.recordedBy.firstName} ${expense.recordedBy.lastName || ""}`.trim()
                  : "—"}
              </DetailRow>
              <DetailRow label="Created At">
                {expense.createdAt
                  ? format(new Date(expense.createdAt), "MMM dd, yyyy 'at' h:mm a")
                  : "—"}
              </DetailRow>
            </div>
          </div>

          {/* Payment Proof */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              Payment Proof
            </h3>
            {expense.hasProof ? (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-start gap-4">
                  {isImage ? (
                    <div className="h-16 w-16 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                      <img
                        src={proofUrl}
                        alt="Proof thumbnail"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-7 w-7 text-emerald-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 break-words">
                      {expense.proofFileName || "Proof file"}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      {expense.proofSize && (
                        <span className="text-xs text-slate-400">
                          {(expense.proofSize / 1024).toFixed(1)} KB
                        </span>
                      )}
                      {expense.proofMimeType && (
                        <span className="text-xs text-slate-400 uppercase">
                          {expense.proofMimeType.replace("image/", "").replace("application/", "")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200">
                  <a
                    href={proofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View
                  </a>
                  <a
                    href={proofUrl}
                    download
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 px-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <Paperclip className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">No payment proof uploaded</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {expense.notes && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Notes
              </h3>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                  {expense.notes}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end rounded-b-2xl">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-200 text-slate-600 hover:bg-slate-100 h-10 px-5"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-900 min-w-0">{children}</span>
    </div>
  );
}
